/**
 * 017: structure copied from
 * vendor/isomorphic/apps/isomorphic/src/app/shared/ecommerce/order/order-list/columns.tsx — same
 * shape every list here uses (identity cell, sized columns, a View action). No template
 * inventory/stock list exists to copy from directly; the row shape and figures are 007-inventory's
 * own (spec §16/§17).
 *
 * Only the data, columns and labels are ours. The screen-to-template mapping is
 * specs/017-isomorphic-theme-only/contracts/screen-map.md.
 */
import type { ColumnDef } from "@tanstack/react-table";
import { Link } from "react-router-dom";
import { Badge, Flex, Text } from "rizzui";
import { PiArrowsDownUpDuotone as MovementIcon, PiEyeDuotone as EyeIcon } from "react-icons/pi";
import { StockFigure } from "@steelyard/theme";
import { formatQty, qty, type FreeStockView } from "../../lib/services/warehouse";
import type { CatalogueItem } from "../../lib/services/catalog";
import { RowActions } from "../../components/table/row-actions";
import { AvailabilityWithFlag } from "./restock";
import { inventoryPath } from "../../lib/paths";

export type InventoryRow = CatalogueItem & { stock: FreeStockView };

type Availability = { label: string; color: "danger" | "warning" | "success" };

/**
 * What a row's three API figures say at a glance, in words. It reads each figure's sign against
 * zero and nothing else — no subtraction, and the negative cases are exactly the ones
 * `GET /stock/exceptions` flags. "Low stock" is the API's own `lowStock` (the page's out-of-stock
 * warning, converted to the SKU's unit by Warehouse), never a threshold applied here.
 * `null` when a figure did not parse, so an unreadable row carries no label rather than a guessed one.
 */
export function availability(stock: FreeStockView): Availability | null {
  const onHand = qty(stock.qtyStockingUnit);
  const free = qty(stock.freeStock);
  if (onHand === null || free === null) return null;
  if (onHand < 0) return { label: "Negative balance", color: "danger" };
  if (free < 0) return { label: "Over-reserved", color: "danger" };
  if (onHand === 0) return { label: "Out of stock", color: "warning" };
  if (free === 0) return { label: "Fully reserved", color: "warning" };
  if (stock.lowStock) return { label: "Low stock", color: "warning" };
  return { label: "In stock", color: "success" };
}

export function AvailabilityBadge({ stock }: { stock: FreeStockView }) {
  const state = availability(stock);
  if (!state) return <Text className="text-gray-500">—</Text>;
  return (
    <Badge variant="flat" color={state.color} rounded="pill" size="sm">
      {state.label}
    </Badge>
  );
}

/** A quantity column's sort key: the API's own figure read as a number. Shown through `StockFigure` as sent. */
const quantity = (value: number | string | null | undefined) => {
  const parsed = Number(value);
  return value === null || value === undefined || !Number.isFinite(parsed) ? undefined : parsed;
};

/**
 * Every quantity through `StockFigure` (FR-001, FR-002, FR-003, FR-004, FR-012) — on-hand, reserved
 * and free are three separate, labelled columns, never a single merged "available" figure (SC-001).
 * No warehouse column: the single v1 warehouse is named once, in the page's own figures (FR-005).
 */
export const inventoryColumns: ColumnDef<InventoryRow>[] = [
  {
    // No avatar: an initial of the SKU name says nothing; the code and brand do.
    id: "sku",
    accessorKey: "name",
    header: "SKU",
    size: 300,
    cell: ({ row }) => (
      <Flex direction="col" align="start" gap="1">
        <Link to={inventoryPath(row.original.slug)} className="font-semibold text-gray-900 hover:underline">
          {row.original.name}
        </Link>
        <Flex align="center" gap="2" className="flex-wrap">
          <Badge variant="flat" color="secondary" size="sm" rounded="md" className="font-mono">
            {row.original.skuCode}
          </Badge>
          <Text className="text-xs text-gray-500">{row.original.brandName}</Text>
        </Flex>
      </Flex>
    ),
  },
  {
    id: "availability",
    header: "Availability",
    size: 230,
    // The badge, and beside it a separate "Add stock" tag that opens the SKU's In Stock / Quantity popup (restock.tsx).
    cell: ({ row }) => <AvailabilityWithFlag row={row.original} />,
  },
  {
    id: "onHand",
    accessorFn: (item) => quantity(item.stock.qtyStockingUnit),
    sortUndefined: "last",
    meta: { align: "right" },
    header: "On hand",
    size: 140,
    cell: ({ row }) => (
      <StockFigure compact label="On hand" value={formatQty(row.original.stock.qtyStockingUnit)} unit={row.original.stock.stockingUnitCode} />
    ),
  },
  {
    id: "reserved",
    accessorFn: (item) => quantity(item.stock.reservedQty),
    sortUndefined: "last",
    meta: { align: "right" },
    header: "Reserved",
    size: 140,
    cell: ({ row }) => (
      <StockFigure compact label="Reserved" value={formatQty(row.original.stock.reservedQty)} unit={row.original.stock.stockingUnitCode} />
    ),
  },
  {
    id: "free",
    accessorFn: (item) => quantity(item.stock.freeStock),
    sortUndefined: "last",
    meta: { align: "right" },
    header: "Free",
    size: 140,
    cell: ({ row }) => (
      <StockFigure compact label="Free" value={formatQty(row.original.stock.freeStock)} unit={row.original.stock.stockingUnitCode} />
    ),
  },
  {
    id: "pieces",
    // The API's derived count, when it could derive one; a SKU with no conversion factor sorts last.
    accessorFn: (item) => (item.stock.conversionAvailable ? quantity(item.stock.derivedCounts[0]?.quantity) : undefined),
    sortUndefined: "last",
    meta: { align: "right" },
    header: "Piece equivalent",
    size: 180,
    cell: ({ row }) => {
      const stock = row.original.stock;
      const derived = stock.derivedCounts[0];
      return stock.conversionAvailable && derived ? (
        <StockFigure compact label="Pieces" value={formatQty(derived.quantity)} unit={derived.unitCode} />
      ) : (
        <StockFigure compact label="Pieces" unavailableReason="no conversion factor set for this SKU" />
      );
    },
  },
  {
    id: "actions",
    header: "",
    size: 110,
    // The menu's one item opens the stock-movement report narrowed to this SKU
    // (`reports/StockMovement.tsx` reads `skuId` from the URL).
    cell: ({ row }) => (
      <RowActions
        name={row.original.skuCode}
        primary={{ label: "View stock position", icon: EyeIcon, to: inventoryPath(row.original.slug) }}
        more={[{ label: "Stock movement report", icon: MovementIcon, to: `/reports/stock-movement?skuId=${row.original.skuId}` }]}
      />
    ),
  },
];
